export type AvatarHairStyle = 'short' | 'crop' | 'curly' | 'side' | 'bun'
export type AvatarFaceShape = 'round' | 'oval' | 'soft-square'
export type AvatarEyeStyle = 'normal' | 'happy' | 'wide'
export type AvatarMouthStyle = 'smile' | 'neutral' | 'grin'
export type AvatarFacialHair = 'none' | 'stubble' | 'moustache'
export type AvatarAccessory = 'none' | 'glasses' | 'round-glasses'

export type AvatarConfig = {
  background: string
  skin: string
  faceShape: AvatarFaceShape
  hairStyle: AvatarHairStyle
  hairColor: string
  eyeStyle: AvatarEyeStyle
  eyeColor: string
  mouthStyle: AvatarMouthStyle
  facialHair: AvatarFacialHair
  shirtColor: string
  accessory: AvatarAccessory
}

export type AvatarPart = 'background' | 'face' | 'hair' | 'eyes' | 'mouth' | 'facialHair' | 'shirt' | 'accessory'

export const AVATAR_PREFIX = 'avatar:v1:'

export const AVATAR_COLORS = {
  background: ['#e4eee7', '#e4edf2', '#f0e9e2', '#ede7ef', '#f1ecdf', '#f0e5e6', '#e7efe2', '#ece8df'],
  skin: ['#f2c6a8', '#e7a37f', '#d89d7a', '#c78663', '#bf7c52', '#9f674a', '#7c4e39'],
  hair: ['#20211e', '#33241f', '#5a3b2c', '#8a552e', '#bd5f32', '#d39a55', '#c8c3b7', '#6c584c'],
  eyes: ['#35231d', '#4e392c', '#4a6572', '#4f6c4f', '#2c3136'],
  shirt: ['#4b7665', '#3e627a', '#806b5c', '#7d5542', '#755d7f', '#a27a43', '#526e86', '#6e7a4f', '#8e5c68'],
} as const

export const HAIR_STYLES: AvatarHairStyle[] = ['short', 'crop', 'curly', 'side', 'bun']
export const FACE_SHAPES: AvatarFaceShape[] = ['round', 'oval', 'soft-square']
export const EYE_STYLES: AvatarEyeStyle[] = ['normal', 'happy', 'wide']
export const MOUTH_STYLES: AvatarMouthStyle[] = ['smile', 'neutral', 'grin']
export const FACIAL_HAIR_STYLES: AvatarFacialHair[] = ['none', 'stubble', 'moustache']
export const ACCESSORIES: AvatarAccessory[] = ['none', 'glasses', 'round-glasses']

function normalizeName(name: string): string {
  return name.trim().toLocaleLowerCase().replace(/\s+/g, ' ')
}

export function avatarSeed(name: string): number {
  let hash = 2166136261
  for (const character of normalizeName(name)) {
    hash ^= character.codePointAt(0) ?? 0
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

function pick<T>(values: readonly T[], seed: number, shift: number): T {
  return values[(seed >>> shift) % values.length]
}

export function generatedAvatarConfig(name: string): AvatarConfig {
  const seed = avatarSeed(name)
  return {
    background: pick(AVATAR_COLORS.background, seed, 0),
    skin: pick(AVATAR_COLORS.skin, seed, 3),
    faceShape: pick(FACE_SHAPES, seed, 6),
    hairStyle: pick(HAIR_STYLES, seed, 9),
    hairColor: pick(AVATAR_COLORS.hair, seed, 12),
    eyeStyle: pick(EYE_STYLES, seed, 15),
    eyeColor: pick(AVATAR_COLORS.eyes, seed, 18),
    mouthStyle: pick(MOUTH_STYLES, seed, 21),
    facialHair: pick(FACIAL_HAIR_STYLES, seed, 23),
    shirtColor: pick(AVATAR_COLORS.shirt, seed, 25),
    accessory: seed % 5 === 0 ? pick(ACCESSORIES, seed, 27) : 'none',
  }
}

export function parseAvatarConfig(value: string | undefined, name: string): AvatarConfig {
  if (!value?.startsWith(AVATAR_PREFIX)) return generatedAvatarConfig(name)
  try {
    const parsed = JSON.parse(decodeURIComponent(value.slice(AVATAR_PREFIX.length))) as Partial<AvatarConfig>
    return { ...generatedAvatarConfig(name), ...parsed }
  } catch {
    return generatedAvatarConfig(name)
  }
}

export function serializeAvatarConfig(config: AvatarConfig): string {
  return `${AVATAR_PREFIX}${encodeURIComponent(JSON.stringify(config))}`
}

export function isCustomAvatar(value?: string): boolean {
  return Boolean(value?.startsWith(AVATAR_PREFIX))
}

export function cycleAvatarValue<T>(values: readonly T[], current: T): T {
  const index = values.indexOf(current)
  return values[(index + 1 + values.length) % values.length]
}

export function randomAvatarConfig(name = `${Date.now()}`): AvatarConfig {
  const base = generatedAvatarConfig(`${name}:${Math.random()}:${Date.now()}`)
  return base
}
